/**
 * T27 — Construção do ReportModel unificado.
 *
 * Privacidade: nenhuma função aqui emite CPF, CNPJ, nome ou razão social.
 * Apenas `employerAnonymousId` (já anonimizado via AnonymousId) pode aparecer.
 */

import type { DiagnosticResult } from '../diagnostic.types';
import type { ValidationSummary } from '../validation/validation.types';
import type { Recommendation } from '../recommendations/recommendation.types';
import type { DiagnosticAssumptions } from '../domain/canonical.types';
import type { ReportModelT27, ReportSectionT27 } from './report.types';
import { selectExecutiveHighlights } from './executiveHighlights';
import { formatScore } from '../format/numberFormat';

// =========================================================================
// Constantes públicas
// =========================================================================

export const REPORT_VERSION = '1.0.0';

export const PILOT_NOTICE =
  'Modelo piloto, calibragem em curso. Use como referência, não como avaliação definitiva.';

// =========================================================================
// Helpers internos
// =========================================================================

/** Formata LocalDate "YYYY-MM-DD" → "DD/MM/YYYY". */
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Gera "YYYY-MM-DDTHH:mm" no fuso local. */
function localIsoNow(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/** Mapeia banda de maturidade para rótulo amigável. */
const BAND_LABEL: Record<string, string> = {
  critico: 'Crítico',
  atencao: 'Atenção',
  consistente: 'Consistente',
  exemplar: 'Exemplar',
};

// =========================================================================
// Builders de seção individual
// =========================================================================

function buildCoverSection(result: DiagnosticResult): ReportSectionT27 {
  const { period } = result.dataset;
  const { score, metrics, validation } = result;
  const analyzedEmployees = metrics.visibility.analyzedEmployees.value;

  const bandLabel = BAND_LABEL[score.band] ?? score.band;
  const limitationNote = validation.hasBlocking
    ? 'Atenção: diagnóstico com issues bloqueantes; use com ressalvas.'
    : validation.confidence === 'high'
      ? 'Diagnóstico com alta confiabilidade.'
      : 'Diagnóstico com confiabilidade moderada; verifique as limitações.';

  return {
    kind: 'cover',
    title: 'Diagnóstico de Jornada',
    content: {
      period: `${fmtDate(period.start)} a ${fmtDate(period.end)}`,
      analyzedEmployees,
      scoreTotal: score.total,
      scoreBand: bandLabel,
      limitationNote,
    },
  };
}

function buildExecutiveSummarySection(result: DiagnosticResult): ReportSectionT27 {
  const highlights = selectExecutiveHighlights(result);
  return {
    kind: 'executive-summary',
    title: 'Resumo Executivo',
    intro: 'Principais números do diagnóstico, em ordem de prioridade.',
    content: { highlights },
  };
}

function buildBaseSection(result: DiagnosticResult): ReportSectionT27 {
  const { visibility } = result.metrics;
  return {
    kind: 'base',
    title: 'Base Analisada',
    intro: 'Visibilidade geral dos dados do período.',
    content: {
      analyzedEmployees: visibility.analyzedEmployees.value,
      analyzedDays: visibility.analyzedDays.value,
      totalPunches: visibility.totalPunches.value,
      completeJourneys: visibility.completeJourneys.value,
      pendingJourneys: visibility.pendingJourneys.value,
    },
  };
}

function buildMetricsSection(result: DiagnosticResult): ReportSectionT27 {
  const { cost, absence, rework, risk } = result.metrics;

  // Contagem total de sinais de risco
  const totalRiskSignals =
    risk.inconsistentPairs.length +
    risk.excessiveJourneys.length +
    risk.shortIntradayBreaks.length +
    risk.shortInterjourneyBreaks.length +
    risk.potentialBritishPoint.length +
    risk.documentIntegrity.length;

  return {
    kind: 'metrics',
    title: 'Métricas Detalhadas',
    intro: 'Resultado por categoria: custo, ausências, retrabalho e risco.',
    content: {
      cost: {
        excessMinutes: cost.excessMinutes.value,
        excessMinutesPerEmployee: cost.excessMinutesPerEmployee.value,
        estimatedExcessCost: cost.estimatedExcessCost
          ? {
              value: cost.estimatedExcessCost.value,
              rangeLow: cost.estimatedExcessCost.rangeLow,
              rangeHigh: cost.estimatedExcessCost.rangeHigh,
              assumptionsUsed: cost.estimatedExcessCost.assumptionsUsed,
            }
          : null,
      },
      absence: {
        shortJourneys: absence.shortJourneys.value,
        shortJourneyMinutes: absence.shortJourneyMinutes.value,
        faltas: absence.faltas.value,
      },
      rework: {
        inferredManualPunches: rework.inferredManualPunches.value,
        inferredDisregardedPunches: rework.inferredDisregardedPunches.value,
        adjustmentRate: rework.adjustmentRate.value,
      },
      risk: {
        totalRiskSignals,
        inconsistentPairsCount: risk.inconsistentPairs.length,
        excessiveJourneysCount: risk.excessiveJourneys.length,
        shortIntradayBreaksCount: risk.shortIntradayBreaks.length,
        shortInterjourneyBreaksCount: risk.shortInterjourneyBreaks.length,
        potentialBritishPointCount: risk.potentialBritishPoint.length,
        documentIntegrityCount: risk.documentIntegrity.length,
      },
    },
  };
}

function buildScoreSection(result: DiagnosticResult): ReportSectionT27 {
  const { score } = result;
  const bandLabel = BAND_LABEL[score.band] ?? score.band;
  return {
    kind: 'score',
    title: 'Score de Maturidade',
    intro: `Maturidade classificada como "${bandLabel}" (${formatScore(score.total)}/100).`,
    content: {
      total: score.total,
      band: score.band,
      bandLabel,
      dimensions: score.dimensions.map((d) => ({
        code: d.code,
        label: d.label,
        value: d.value,
        weight: d.weight,
        rationale: d.rationale,
        underdetermined: d.underdetermined,
      })),
      pilot: score.pilot,
    },
  };
}

/**
 * Constrói seção de recomendações.
 * Ordena por prioridade: critical → high → medium → low.
 */
export function buildRecommendationsSection(
  recommendations: Recommendation[],
): ReportSectionT27 {
  const PRIORITY_ORDER: Record<string, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
  };

  const sorted = [...recommendations].sort(
    (a, b) =>
      (PRIORITY_ORDER[b.priority] ?? 0) - (PRIORITY_ORDER[a.priority] ?? 0),
  );

  return {
    kind: 'recommendations',
    title: 'Recomendações',
    intro: 'Sugestões priorizadas para melhoria da gestão de jornada.',
    content: { recommendations: sorted },
  };
}

function buildNextStepsSection(recommendations: Recommendation[]): ReportSectionT27 {
  const PRIORITY_ORDER: Record<string, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
  };

  const top3 = [...recommendations]
    .sort(
      (a, b) =>
        (PRIORITY_ORDER[b.priority] ?? 0) - (PRIORITY_ORDER[a.priority] ?? 0),
    )
    .slice(0, 3)
    .map((r) => ({
      code: r.code,
      nextStep: r.nextStep,
      priority: r.priority,
    }));

  return {
    kind: 'next-steps',
    title: 'Próximos Passos',
    intro: 'Top 3 ações recomendadas para o curto prazo.',
    content: { steps: top3 },
  };
}

/**
 * Agrupa issues com mesmo código para evitar flood no relatório.
 * Mantém a primeira ocorrência e adiciona "(×N)" na mensagem quando há repetições.
 */
function aggregateIssuesByCode(
  rawIssues: ValidationSummary['issues'],
): Array<{ code: string; severity: string; message: string; source: string; recommendation?: string }> {
  const seen = new Map<string, {
    code: string;
    severity: string;
    message: string;
    source: string;
    recommendation?: string;
    count: number;
  }>();
  for (const issue of rawIssues) {
    if (seen.has(issue.code)) {
      seen.get(issue.code)!.count += 1;
    } else {
      seen.set(issue.code, {
        code: issue.code,
        severity: issue.severity,
        message: issue.message,
        source: issue.source,
        recommendation: issue.recommendation,
        count: 1,
      });
    }
  }
  return Array.from(seen.values()).map(({ count, ...rest }) => ({
    ...rest,
    message: count > 1 ? `${rest.message} (×${count} ocorrências)` : rest.message,
  }));
}

/**
 * Constrói seção de limitações a partir do ValidationSummary.
 * Se confidence === 'high' e sem blocking → destaca alta confiabilidade.
 * Issues com mesmo código são agrupadas para evitar flood no relatório.
 */
export function buildLimitationsSection(
  validation: ValidationSummary,
): ReportSectionT27 {
  const isHighConfidence =
    validation.confidence === 'high' && !validation.hasBlocking;

  const introText = isHighConfidence
    ? 'Diagnóstico com alta confiabilidade: nenhuma issue bloqueante encontrada.'
    : validation.hasBlocking
      ? `Diagnóstico com ${validation.blockingCount} issue(s) bloqueante(s). Revise antes de usar como base decisória.`
      : `Diagnóstico com confiabilidade ${validation.confidence}. Verifique as issues listadas.`;

  const issues = aggregateIssuesByCode(validation.issues);

  return {
    kind: 'limitations',
    title: 'Limitações e Confiabilidade',
    intro: introText,
    content: {
      confidence: validation.confidence,
      hasBlocking: validation.hasBlocking,
      blockingCount: validation.blockingCount,
      errorCount: validation.errorCount,
      warningCount: validation.warningCount,
      issues,
    },
  };
}

/**
 * Constrói seção de premissas utilizadas no diagnóstico.
 * Quando assumption não definida, informa "padrão usado".
 */
export function buildAssumptionsSection(
  assumptions: DiagnosticAssumptions,
): ReportSectionT27 {
  const items: Array<{ label: string; value: string }> = [
    {
      label: 'Custo médio por hora (BRL)',
      value:
        assumptions.hourlyRateBrl != null
          ? `R$ ${assumptions.hourlyRateBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/h`
          : 'padrão usado (não informado)',
    },
    {
      label: 'Folha mensal total (BRL)',
      value:
        assumptions.monthlyPayrollBrl != null
          ? `R$ ${assumptions.monthlyPayrollBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          : 'padrão usado (não informado)',
    },
    {
      label: 'Tolerância (minutos)',
      value: `${assumptions.toleranceMinutes} min`,
    },
    {
      label: 'Jornada excessiva (minutos/dia)',
      value: `${assumptions.excessiveJourneyMinutes} min`,
    },
    {
      label: 'Intervalo intrajornada mínimo (minutos)',
      value: `${assumptions.shortIntradayBreakMinutes} min`,
    },
    {
      label: 'Interjornada mínima (minutos)',
      value: `${assumptions.shortInterjourneyMinutes} min`,
    },
    {
      label: 'Início do período noturno (hora)',
      value:
        assumptions.nightStartHour != null
          ? `${assumptions.nightStartHour}h`
          : 'padrão usado (não informado)',
    },
    {
      label: 'Fim do período noturno (hora)',
      value:
        assumptions.nightEndHour != null
          ? `${assumptions.nightEndHour}h`
          : 'padrão usado (não informado)',
    },
  ];

  return {
    kind: 'assumptions',
    title: 'Premissas Utilizadas',
    intro: 'Parâmetros configurados ou assumidos como padrão para este diagnóstico.',
    content: { items },
  };
}

// =========================================================================
// Funções principais exportadas
// =========================================================================

/**
 * Constrói as seções principais do relatório.
 * Ordem: cover, executive-summary, base, metrics, score,
 *        recommendations, next-steps, limitations, assumptions.
 */
export function buildReportSections(
  result: DiagnosticResult,
  assumptions?: DiagnosticAssumptions,
): ReportSectionT27[] {
  const effectiveAssumptions: DiagnosticAssumptions = assumptions ?? {
    dailyHours: 8,
    monthlyHours: 220,
    toleranceMinutes: 5,
    excessiveJourneyMinutes: 600,
    shortIntradayBreakMinutes: 60,
    shortInterjourneyMinutes: 660,
    correctionMinutesPerEvent: 5,
  };

  return [
    buildCoverSection(result),
    buildExecutiveSummarySection(result),
    buildBaseSection(result),
    buildMetricsSection(result),
    buildScoreSection(result),
    buildRecommendationsSection(result.recommendations),
    buildNextStepsSection(result.recommendations),
    buildLimitationsSection(result.validation),
    buildAssumptionsSection(effectiveAssumptions),
  ];
}

/**
 * Monta ReportModelT27 completo a partir de DiagnosticResult + assumptions opcionais.
 *
 * Privacidade: nunca emite CPF, CNPJ, nome, razão social ou nome de arquivo.
 * Apenas `employerAnonymousId` (AnonymousId já anonimizado) pode aparecer.
 */
export function buildReportModel(
  result: DiagnosticResult,
  assumptions?: DiagnosticAssumptions,
): ReportModelT27 {
  const { period } = result.dataset;
  const periodLabel = `${fmtDate(period.start)} a ${fmtDate(period.end)}`;

  // Extrai ID anônimo do empregador (sem CNPJ ou nome real)
  const employerAnonymousId = result.dataset.employer?.identity?.id;

  const sections = buildReportSections(result, assumptions);

  return {
    version: REPORT_VERSION,
    generatedAt: localIsoNow(),
    periodLabel,
    employerAnonymousId,
    sections,
    pilotNotice: PILOT_NOTICE,
  };
}
